import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { useSelector, useDispatch } from 'react-redux';
import {
  clearDetailedAnalysis,
  getDeals,
  removeThreads,
  setCurrentDeal,
  setDataSortCase,
} from '../../../store/deals/deals.actions';
import {
  EngagementParticipant,
  EngagementSubrow,
  EngagementTableRow,
  TableDataSortCase,
  TableRowAccount,
} from '../../../store/deals/deals.types';
import { RootState } from '../../../store/store';
import { User } from '../../../types';
import { LinkToThreadRow } from './components/linkToThread/LinkToThreadRow';
import { ParticipantsRow } from './components/participants/ParticipantsRow';
import { SubjectRow } from './components/subject/SubjectRow';
import { TimeSentRow } from './components/lastMessageSentTime/TimeSentRow';
import { DeleteThread } from './components/deleteThread/DeleteThread';
import { DealsTableHeaderTooltip } from './components/headerTooltip/DealsTableHeaderTooltip';
import { PopupOption } from '../../../components/Popup/PopupOption';
import { Options } from '../../Users/components/Table/components/Options';
import { RenameAccIcon } from './assets/RenameAccIcon';
import { RemoveEngIcon } from './assets/RemoveEngIcon';
import { Account } from './components/tableHeader/Account/Account';
import { defineSortValuesForRequest } from '../../../store/deals/deals.utils';
import { LastInteraction } from './components/tableHeader/LastInteraction/LastInteraction';
import { AccountCell } from './components/account/AccountCell';
import { AccountSubrowCell } from './components/accountSubrow/AccountSubrowCell';

type Column = {
  title: string | JSX.Element | React.FC;
  dataIndex: string;
  key: string; // eslint-disable-next-line
  render?: (...params: any[]) => any;
  shouldCellUpdate: () => boolean;
  width?: string;
  maxHeight?: string;
};

const tooltipAttrs = {
  title: {
    wrapper: { className: 'deals-table-header__title' },
    wrapperCenteredTitle: { className: 'centered deals-table-header__title' },
    lastActive: { className: 'deals-table-header__title-last-active' },
    timeSent: { className: 'deals-table-header__title-time-sent' },
    duration: { className: 'deals-table-header__title-duration' },
    competence: { className: 'deals-table-header__title-competence' },
  },
  subjectLine: {
    text: `This shows the most recent subject line in the engagement. 
    An expanded row shows the matching threads’ subject lines.`,
    width: '200px',
  },
};

const subjectLine = (
  <div {...tooltipAttrs.title.wrapper}>
    <span {...tooltipAttrs.title.lastActive}>Subject Line </span>
    <DealsTableHeaderTooltip {...tooltipAttrs.subjectLine} />
  </div>
);

const selectRowClick = (
  e: React.MouseEvent,
  getSelectedRow: (row: EngagementTableRow) => void,
  row: EngagementTableRow
) => {
  e.stopPropagation();
  getSelectedRow(row);
};

const getColumns = (
  options: JSX.Element[],
  getSelectedRow: (deals: EngagementTableRow) => void,
  sortDataByAccount: () => void,
  sortDataByTime: () => void,
  dataSortCase: TableDataSortCase
): Column[] => [
  {
    title: (
      <Account changeSortCase={sortDataByAccount} dataSortCase={dataSortCase} />
    ),
    dataIndex: 'account',
    key: 'account',
    render: (account: TableRowAccount): JSX.Element => (
      <AccountCell account={account} />
    ),
    shouldCellUpdate: () => true,
  },
  {
    title: 'Participants',
    dataIndex: 'participants',
    key: 'participants',
    render: (participants: EngagementParticipant[]): JSX.Element => (
      <ParticipantsRow participants={participants} />
    ),
    shouldCellUpdate: () => true,
  },
  {
    title: subjectLine,
    dataIndex: 'subject',
    key: 'subject',
    render: (subject: string): JSX.Element => <SubjectRow subject={subject} />,
    shouldCellUpdate: () => true,
  },
  {
    title: (
      <LastInteraction
        changeSortCase={sortDataByTime}
        dataSortCase={dataSortCase}
      />
    ),
    dataIndex: 'lastIterationDate',
    key: 'lastIterationDate',
    render: (lastIterationDate: string): JSX.Element => (
      <TimeSentRow time={lastIterationDate} />
    ),
    shouldCellUpdate: () => true,
  },
  {
    title: '',
    dataIndex: 'engagementId',
    key: 'engagementId',
    render: (
      engagementId: string,
      engagement: EngagementTableRow
    ): JSX.Element => (
      <LinkToThreadRow
        engagementId={engagementId}
        branchId={engagement?.branchId}
      />
    ),
    shouldCellUpdate: () => true,
  },
  {
    title: '',
    dataIndex: 'options',
    key: 'options',
    render: (engagement: EngagementTableRow): JSX.Element => (
      <div
        onClick={(event) => selectRowClick(event, getSelectedRow, engagement)}
        className="popup-icon-wrap"
      >
        <Options options={options} />
      </div>
    ),
    shouldCellUpdate: () => true,
  },
];

const createColumnsForExpandedRow = (): Column[] => {
  return [
    {
      title: '',
      dataIndex: 'isLastSubrow',
      key: 'branchId',
      render: (isLastSubrow: boolean): JSX.Element => (
        <AccountSubrowCell isLastSubrow={isLastSubrow} />
      ),
      shouldCellUpdate: () => false,
    },
    {
      title: '',
      dataIndex: 'contacts',
      key: 'contacts',
      render: (participants: EngagementParticipant[]): JSX.Element => (
        <ParticipantsRow participants={participants} />
      ),
      shouldCellUpdate: () => true,
    },
    {
      title: '',
      dataIndex: 'subject',
      key: 'subject',
      render: (subject: string): JSX.Element => (
        <SubjectRow subject={subject} />
      ),
      shouldCellUpdate: () => true,
    },

    {
      title: '',
      dataIndex: 'lastIterationDate',
      key: 'lastIterationDate',
      render: (lastIterationDate: string): JSX.Element => (
        <TimeSentRow time={lastIterationDate} />
      ),
      shouldCellUpdate: () => true,
    },
    {
      title: '',
      dataIndex: 'engagementId',
      key: 'engagementId',
      render: (
        engagementId: string,
        engagement: EngagementTableRow
      ): JSX.Element => (
        <LinkToThreadRow
          engagementId={engagementId}
          branchId={engagement?.branchId}
        />
      ),
      shouldCellUpdate: () => true,
    },
    {
      title: '',
      dataIndex: 'rowToSelect',
      key: 'rowToSelect',
      render: (): JSX.Element => <DeleteThread hidden />,
      shouldCellUpdate: () => true,
    },
  ];
};

type DealsTable = {
  cols: Column[];
  createColumnsForExpandedRow: (row: EngagementSubrow[]) => Column[];
  dealsList: EngagementTableRow[];
  rowSelection: {
    onChange: (keys: React.Key[], rows: EngagementTableRow[]) => void;
  };
  subrowSelection: {
    onChange: (keys: React.Key[], rows: EngagementSubrow[]) => void;
  };
  selectedRows: EngagementTableRow[];
  user: User | null;
  currentDealsQty: number;
  totalDealsQty: number;
  currentPageNumber: number;
  deleteModalOpen: boolean;
  toggleDeleteModal: () => void;
  closeModalAfterSuccess: () => void;
  removeDeal: () => void;
  toggleRenameModal: () => void;
  renameModalOpen: boolean;
  currentDeal: EngagementTableRow | null;
  loadPreviousPage: () => void;
  loadNextPage: () => void;
  loading: boolean;
  dealsOpenedFirts: boolean;
  dealsNoData: boolean;
};

export const useDealsTable = (): DealsTable => {
  const {
    currentDeal,
    isLoading,
    dealsNoData,
    dealsOpenedFirts,
    engagements,
    requestedPageNumber,
    dataSortCase,
    requestedEngsQty,
    neverAnalyzed,
  } = useSelector((state: RootState) => state.deals);

  const { user } = useSelector((state: RootState) => state.auth);

  const [selectedRows, setSelectedRows] = useState<EngagementTableRow[]>([]);

  const [selectedSubrows, setselectedSubrows] = useState<EngagementSubrow[]>(
    []
  );

  const [deleteModalOpen, setDeleteModalOpen] = useState(false);

  const [renameModalOpen, setRenameModalOpen] = useState(false);

  const dispatch = useDispatch();

  const totalDealsQty = engagements?.count ? engagements.count : 0;
  const currentDealsQty = requestedEngsQty;

  const currentPageNumber = engagements?.page ? engagements.page : 0;

  const dealsList = engagements?.results ? engagements?.results : [];

  const loading = isLoading.deals;

  const { sortBy, order } = defineSortValuesForRequest(dataSortCase);

  useEffect(() => {
    dispatch(clearDetailedAnalysis());

    if (user?.orgId && user?.userId) {
      dispatch(
        getDeals(
          user.orgId,
          user.userId,
          requestedPageNumber,
          requestedEngsQty,
          sortBy,
          order
        )
      );
    } // eslint-disable-next-line
  }, []);

  const toggleDeleteModal = () => {
    setDeleteModalOpen(!deleteModalOpen);

    if (deleteModalOpen && currentDeal) {
      setTimeout(() => {
        dispatch(setCurrentDeal(null));
      }, 200);
    }
  };

  const closeModalAfterSuccess = () => {
    if (deleteModalOpen) {
      setDeleteModalOpen(false);
      if (selectedRows.length) {
        setSelectedRows([]);
      }

      if (currentDeal) {
        setTimeout(() => {
          dispatch(setCurrentDeal(null));
        }, 200);
      }
    }
  };

  const removeDeal = () => {
    if (user?.orgId && user?.userId && deleteModalOpen) {
      const { userId, orgId } = user;
      if (currentDeal) {
        dispatch(
          removeThreads(
            orgId,
            userId,
            [currentDeal.engagementId],
            currentPageNumber,
            requestedEngsQty,
            sortBy,
            order
          )
        );
      } else if (selectedRows.length) {
        const selectedThreadsId = selectedRows.map((el) => el.engagementId);
        dispatch(
          removeThreads(
            orgId,
            userId,
            selectedThreadsId,
            currentPageNumber,
            requestedEngsQty,
            sortBy,
            order
          )
        );
      }
    }
  };

  const toggleRenameModal = () => {
    setRenameModalOpen(!renameModalOpen);

    if (renameModalOpen && currentDeal) {
      setTimeout(() => {
        dispatch(setCurrentDeal(null));
      }, 200);
    }
  };

  const handleChooseDeal = useCallback(
    (choosedDeal: EngagementTableRow): void => {
      dispatch(setCurrentDeal(choosedDeal));
    },
    [dispatch, currentDeal, engagements] // eslint-disable-line
  );

  const loadPreviousPage = () => {
    if (user?.orgId && user?.userId && currentPageNumber > 1) {
      const previosPageNumber = currentPageNumber - 1;
      dispatch(
        getDeals(
          user.orgId,
          user.userId,
          previosPageNumber,
          requestedEngsQty,
          sortBy,
          order
        )
      );
      if (selectedRows.length) {
        setSelectedRows([]);
      }
    }
  };

  const loadNextPage = () => {
    const totalPages = totalDealsQty
      ? Math.ceil(totalDealsQty / requestedEngsQty)
      : 0;

    if (user?.orgId && user?.userId && currentPageNumber < totalPages) {
      const nextPageNumber = currentPageNumber + 1;

      dispatch(
        getDeals(
          user.orgId,
          user.userId,
          nextPageNumber,
          requestedEngsQty,
          sortBy,
          order
        )
      );

      if (selectedRows.length) {
        setSelectedRows([]);
      }
    }
  };

  const handleSelect = (
    keys: React.Key[],
    rows: EngagementTableRow[]
  ): void => {
    setSelectedRows(rows);
  };

  const handleSelectSubrows = (
    keys: React.Key[],
    rows: EngagementSubrow[]
  ): void => {
    setselectedSubrows(rows);
  };

  const options: JSX.Element[] = useMemo(
    () => [
      ...(!selectedRows.length
        ? [
            <PopupOption
              key="Rename Account"
              className="options__option"
              text="Rename Account"
              onChoose={() => {
                toggleRenameModal();
              }}
              Icon={RenameAccIcon}
              disabled={neverAnalyzed}
            />,
          ]
        : []),
      <PopupOption
        key="Remove Engagement"
        className="options__option"
        text="Remove Engagement"
        onChoose={() => {
          toggleDeleteModal();
        }}
        Icon={RemoveEngIcon}
        disabled={neverAnalyzed}
      />,
    ],
    // eslint-disable-next-line
    [user?.userId, selectedRows?.length, neverAnalyzed]
  );

  const sortDataByAccount = () => {
    if (user?.orgId && user?.userId) {
      if (dataSortCase !== 'a-z') {
        const { sortBy: sort, order: sortOrder } = defineSortValuesForRequest(
          'a-z'
        );

        dispatch(setDataSortCase('a-z'));

        dispatch(
          getDeals(
            user.orgId,
            user.userId,
            1,
            requestedEngsQty,
            sort,
            sortOrder
          )
        );
      } else if (dataSortCase !== ('z-a' as TableDataSortCase)) {
        const { sortBy: sort, order: sortOrder } = defineSortValuesForRequest(
          'z-a'
        );

        dispatch(setDataSortCase('z-a'));

        dispatch(
          getDeals(
            user.orgId,
            user.userId,
            1,
            requestedEngsQty,
            sort,
            sortOrder
          )
        );
      }
    }
  };

  const sortDataByTime = () => {
    if (user?.orgId && user?.userId) {
      if (dataSortCase !== '1-9') {
        const { sortBy: sort, order: sortOrder } = defineSortValuesForRequest(
          '1-9'
        );

        dispatch(setDataSortCase('1-9'));
        dispatch(
          getDeals(
            user.orgId,
            user.userId,
            1,
            requestedEngsQty,
            sort,
            sortOrder
          )
        );
      } else if (dataSortCase !== ('9-1' as TableDataSortCase)) {
        const { sortBy: sort, order: sortOrder } = defineSortValuesForRequest(
          '9-1'
        );

        dispatch(setDataSortCase('9-1'));
        dispatch(
          getDeals(
            user.orgId,
            user.userId,
            1,
            requestedEngsQty,
            sort,
            sortOrder
          )
        );
      }
    }
  };

  const cols = useMemo(
    () =>
      getColumns(
        options,
        handleChooseDeal,
        sortDataByAccount,
        sortDataByTime,
        dataSortCase
      ), // eslint-disable-next-line
    [handleChooseDeal, options, engagements, currentDeal]
  );

  const rowSelection = {
    selectedRowKeys: selectedRows?.map((el) => el.engagementId),
    onChange: handleSelect,
  };

  const subrowSelection = {
    selectedRowKeys: selectedSubrows?.map((el) => el.platformThreadId),
    onChange: handleSelectSubrows,
  };

  return {
    dealsList,
    cols,
    rowSelection,
    selectedRows,
    subrowSelection,
    user,
    currentPageNumber,
    currentDealsQty,
    totalDealsQty,
    deleteModalOpen,
    toggleDeleteModal,
    closeModalAfterSuccess,
    removeDeal,
    renameModalOpen,
    toggleRenameModal,
    currentDeal,
    loadPreviousPage,
    loadNextPage,
    loading,
    dealsNoData,
    dealsOpenedFirts,
    createColumnsForExpandedRow,
  };
};
